import * as React from 'react';
import {
  ReactElement, useCallback, useMemo, useState,
} from 'react';
import CustomPieChart from './PieChart/PieChart';
import {
  createLocaleContext,
  LocaleContext,
  Texts,
} from '../../shared/context/LocaleContext';
import { MultiValuesNode } from '../../types/chartDataTypes';
import { Container, ChartWrapper, PieWrapper } from './IncomeStatement.styles';
import DataTable from '../../shared/components/DataTable/DataTable';
import {
  findEntryWithName,
  getLeafNodes,
} from '../../shared/helper/chartDataHelper';
import TableAndChart from '../../shared/components/TableAndChart/TableAndChart';
import DataTableModal from '../../shared/components/DataTableModal/DataTableModal';
import { prepareData } from './IncomeStatement.helper';
import Header from '../../shared/components/Header/Header';
import { getUrlForKey } from '../../shared/helper/links';

/**
 * Will render a table and a chart for each of the entries in the data node.
 * The total of the data node will be shown in the last table.
 */
export type IncomeStatementProps = {
  data: MultiValuesNode;
  notesLabel: string;
  chartDataLabel: string;
  dataColumns: string[];
  unitInfo: string;
  notesUrlFallback: string;
  notesUrlMapping: {
    [note: string]: string;
  };
  language?: string;
  texts: Texts;
};

/** Data below this level is not shown in the table and only this level will get a detail layer */
const MAX_TABLE_LEVEL = 3;

const IncomeStatement = ({
  data,
  notesLabel,
  chartDataLabel,
  dataColumns,
  unitInfo,
  notesUrlFallback,
  notesUrlMapping,
  language = 'en',
  texts,
}: IncomeStatementProps): React.ReactElement => {
  const [modalContent, setModalContent] = useState(null as MultiValuesNode);
  const [markedEntry, setMarkedEntry] = useState(null as string);

  const localeContext = createLocaleContext(language, texts);

  const chartDataColumns = [notesLabel, chartDataLabel];

  const chartData = useMemo(
    () => prepareData(data, notesLabel, chartDataLabel),
    [data, notesLabel, chartDataLabel],
  );

  const highlightEntry = useCallback((entryTitle: string): void => {
    const isTopLevelEntry = chartData.allTableData.entries.some((entry) => {
      if (entry.name === entryTitle || entry.totalLabel === entryTitle) {
        return true;
      }
      // we do not want to highlight the whole pie chart when we have only one group entry...
      if (entry.entries?.length === 1) {
        const singleSubEntry = entry.entries[0];
        if (singleSubEntry.name === entryTitle || singleSubEntry.totalLabel === entryTitle) {
          return true;
        }
      }
      return false;
    });
    if (!isTopLevelEntry) {
      setMarkedEntry(entryTitle);
    }
  }, [chartData, setMarkedEntry]);

  const onChartCellClick = useCallback((dataName: string): void => {
    const completeEntry = findEntryWithName(data, dataName);
    if (completeEntry) {
      const modalData = {
        name: completeEntry.name,
        totalLabel: completeEntry.totalLabel,
        values: completeEntry.values,
        entries: getLeafNodes(completeEntry),
      } as MultiValuesNode;

      // TODO: For the 2020 data this is the exact position where entriesSignReversal occurs
      // Would be better to handle the flag for the whole data structure
      if (completeEntry.entriesSignReversal) {
        modalData.values = {};
        dataColumns.forEach((column) => {
          modalData.values[column] = completeEntry.values[column] * -1;
        });
      }
      setModalContent(modalData);
    }
  }, [data, chartDataLabel, setModalContent]);

  const onEntryClick = useCallback((entry: MultiValuesNode): void => (
    onChartCellClick(entry.totalLabel || entry.name)
  ), [onChartCellClick]);

  const entryShouldOpenModal = useCallback((searchLabel: string, level: number): boolean => {
    const completeEntry = findEntryWithName(chartData.allTableData, searchLabel);
    return completeEntry && level === MAX_TABLE_LEVEL;
  }, [chartData]);

  const entryClickTargetBuilder = useCallback((entry: MultiValuesNode, column: string, level: number) => {
    if (column === notesLabel) {
      return getUrlForKey(entry.values[notesLabel], notesUrlMapping, notesUrlFallback);
    }
    if (!entryShouldOpenModal(entry.totalLabel || entry.name, level)) {
      return undefined;
    }
    return () => {
      onEntryClick(entry);
    };
  }, [notesUrlMapping, notesUrlFallback, notesLabel, entryShouldOpenModal, onEntryClick]);

  return (
    <LocaleContext.Provider value={localeContext}>
      <Container>
        <TableAndChart>
          {chartData.charts.reduce((elements, chart): ReactElement[] => [
            ...elements,
            <DataTable
              key={`table${chart.tableHeadline}`}
              data={chart.tableData}
              columnKeys={chartDataColumns}
              headline={chart.tableHeadline}
              note={unitInfo}
              expandFirstHeader
              maxLevel={MAX_TABLE_LEVEL}
              markEntryWithName={markedEntry}
              onLineHover={highlightEntry}
              entryClickTargetBuilder={entryClickTargetBuilder}
            />,
            <ChartWrapper key={`chart${chart.tableHeadline}`}>
              <Header
                headline={`${chart.tableHeadline} ${chartDataLabel}`}
                mobileOnlyHeadline
              />
              <PieWrapper>
                <CustomPieChart
                  data={chart.pieData}
                  innerLabel={unitInfo}
                  highlightDataName={markedEntry}
                  onDataHover={highlightEntry}
                  onDataClick={onChartCellClick}
                  hideLabel
                />
              </PieWrapper>
            </ChartWrapper>,
          ], [] as ReactElement[])}
        </TableAndChart>

        <DataTableModal
          dataNode={modalContent}
          dataColumns={dataColumns}
          columnLabels={!dataColumns || dataColumns.length === 1 ? { [chartDataLabel]: unitInfo } : undefined}
          firstColumnLabel={dataColumns && dataColumns.length > 1 ? unitInfo : undefined}
          headlinePrefix={dataColumns.length === 1 ? dataColumns[0] : undefined}
          onRequestClose={() => setModalContent(null)}
        />
      </Container>
    </LocaleContext.Provider>
  );
};

export default IncomeStatement;
